/**
 * "The bag is empty" — the operator's acknowledgement of a dustbag
 * task, offered wherever the waiting robot or its task can be seen
 * (the Fleet drawer's Current task section, and the Tasks drawer).
 *
 * WHAT IT ENDS. A `dustbag` task drives the robot to a DustBagStation
 * and then WAITS: the fleet adapter's `PerformAction` execution sits on
 * an ack for up to the task's wait (the site's default, 30 minutes
 * unless the site set another, or what the dispatch form was given),
 * raising an alert asking someone to empty the bag. This press is what
 * lands on `robot_action_confirmations` and releases it. On timeout the
 * task completes anyway, flagged "not confirmed — timed out", because a
 * robot must never be stranded in a corridor waiting for a person who
 * went home.
 *
 * ADDRESSES THE ROBOT, carrying `task_id` when the caller knows it.
 * That asymmetry is the design, not a shortcut: an emergency interrupt
 * gives the adapter no callback and re-invokes `execute_action` FROM
 * SCRATCH on resume, so the confirm has to be something the adapter can
 * match to a robot it is holding rather than to one execution object it
 * may already have thrown away. The task id, when present, is what lets
 * it latch the ack against the right task.
 *
 * ADMISSION IS NOT THE OUTCOME, the same rule the lift verbs and the
 * task controls are built on — so this renders `accepted` as an explicit
 * "not confirmed" rather than as a success, and says what to watch
 * instead. It reuses `task-command-verdict.ts` for that reading, which
 * is where the 409 envelope's real `CommandRecord` is dug out from.
 */
import { useQuery } from '@tanstack/react-query';
import { RoleGuard } from '../../auth/RoleGuard';
import { ROBOT_CONFIRM_DUSTBAG_ROLES } from '../../auth/types';
import { useConfirmDustbag } from '../../api/hooks/useCommissionRobot';
import { useSafeSiteId } from '../../state/useActiveSite';
import { capabilitiesQueryOptions } from '../../api/query-options';
import { networkFailureText } from '../../api/v1-client';
import type { CommandRecord, SiteId } from '../../api/v1';
import {
  refusedCommandFromError,
  verdictFromError,
  type OperatorVerdict,
} from '../Tasks/task-command-verdict';
import '../Tasks/TaskDetailDrawer.css';

/**
 * What the `CommandRecord` means here, in the operator's terms.
 *
 * Deliberately NOT `taskCommandOutcome`: that function's pending copy
 * is written about stopping a task ("the task is not stopped until its
 * state changes"), which is the opposite of what this verb does. The
 * SHAPE is the same and the classification rule is the same —
 * `observed_applied` is the only state that means done — so only the
 * words differ.
 */
function dustbagOutcome(command: CommandRecord): OperatorVerdict | { kind: 'pending'; message: string; detail: string | null } {
  const detail = command.outcome_message ?? null;
  const base = {
    verb: 'Confirm routine check done',
    request_id: command.correlation?.rmf_request_id ?? null,
    wait_name: null,
    phase: null,
    ts: Date.now(),
    preflight: false,
  } as const;

  if (command.state === 'observed_applied') {
    return {
      kind: 'verdict',
      ...base,
      status: 'done',
      code: null,
      reason: detail ?? 'The robot has been told the routine check is done.',
    };
  }
  if (command.state === 'rejected') {
    return {
      kind: 'verdict',
      ...base,
      status: 'refused',
      code: 'REJECTED',
      reason: detail ?? 'The confirmation was refused.',
    };
  }
  if (command.state === 'timed_out' || command.state === 'outcome_unknown') {
    return {
      kind: 'verdict',
      ...base,
      status: 'timeout',
      code: command.state === 'outcome_unknown' ? 'OUTCOME_UNKNOWN' : 'TIMED_OUT',
      reason:
        detail ??
        'Nothing answered. The robot may still be waiting; watch the task, and if it is ' +
          'still waiting in a minute, press again.',
    };
  }
  return {
    kind: 'pending',
    // No topic name: the front desk reads this (P9, 2026-10-31).
    message:
      'Sent, but NOT confirmed yet. The task carries on once the robot moves off. ' +
      'If it is still waiting in a minute, press again.',
    detail,
  };
}

export function DustbagConfirmAction({
  fleet,
  robot,
  taskId,
}: {
  fleet: string;
  robot: string;
  /** Omitted when the console does not know which task is waiting.
   *  Not sent as null — see `confirmDustbag`. */
  taskId?: string;
}) {
  const confirm = useConfirmDustbag();
  const siteId = useSafeSiteId();
  const capabilities = useQuery(capabilitiesQueryOptions(siteId ? (siteId as SiteId) : null));
  const cap = (capabilities.data?.capabilities ?? []).find(
    (c) => c.capability === 'robot.confirm_dustbag',
  );
  // Same gate `TaskControlActions` and `CommissionButton` apply: only
  // disabled once the capability probe has actually ANSWERED, so a
  // slow or failed probe leaves the action available and the server
  // stays the authority on the refusal. A robot parked in a corridor
  // is the wrong place to discover that a probe timed out.
  const disabled = confirm.isPending || (capabilities.data !== undefined && cap?.enabled === false);

  // The press this banner belongs to. Read off the mutation's own
  // `variables` rather than a second piece of state, so it can never
  // sit under a different robot from the one it reports on — this
  // component is re-used IN PLACE when the robot or task under it
  // changes (the Fleet drawer re-derives `currentTask` live and gives
  // this no `key`), exactly the swap that put a green "done" under the
  // wrong task in `TaskControlActions` before its own guard.
  const sentFor = confirm.variables;
  const belongsHere = sentFor?.fleet === fleet && sentFor?.robot === robot;
  const outcome = (() => {
    if (!belongsHere) return null;
    if (confirm.isError) {
      const refused = refusedCommandFromError(confirm.error);
      if (refused) return dustbagOutcome(refused);
      // A request the browser never sent (a refused CORS preflight,
      // DNS, a dropped connection) is said as that and not as a
      // refusal — there was no HTTP status to refuse with.
      const blocked = networkFailureText(confirm.error);
      const verdict = verdictFromError('Confirm routine check done', confirm.error);
      return blocked ? { ...verdict, reason: blocked } : verdict;
    }
    return confirm.data ? dustbagOutcome(confirm.data.command) : null;
  })();

  return (
    <RoleGuard allowedRoles={[...ROBOT_CONFIRM_DUSTBAG_ROLES]}>
      <div className="nx-task-drawer__actions">
        <button
          type="button"
          className="nx-task-drawer__action"
          onClick={() => confirm.mutate({ fleet, robot, ...(taskId ? { taskId } : {}) })}
          disabled={disabled}
          title={
            cap?.enabled === false
              ? cap.disabled_reasons.join(', ')
              : `Tell ${robot} the routine check is done so it can finish this task.`
          }
          data-testid="dustbag-confirm-btn"
        >
          {confirm.isPending ? 'Sending…' : 'Confirm routine check done'}
        </button>
      </div>
      {outcome?.kind === 'pending' && (
        <div
          className="nx-task-drawer__banner nx-task-drawer__banner--pending"
          data-testid="dustbag-confirm-pending"
          data-state={confirm.data?.command.state ?? ''}
          role="status"
        >
          <strong>⏳ Not confirmed</strong>
          <p>{outcome.message}</p>
          {outcome.detail && <p className="nx-task-drawer__sub">{outcome.detail}</p>}
        </div>
      )}
      {outcome?.kind === 'verdict' && (
        <div
          className={`nx-task-drawer__banner nx-task-drawer__banner--${outcome.status}`}
          data-testid="dustbag-confirm-verdict"
          data-status={outcome.status}
          data-code={outcome.code ?? ''}
          // A refusal must interrupt a screen reader: it contradicts
          // the button just pressed. Only "done" is conversational.
          role={outcome.status === 'done' ? 'status' : 'alert'}
        >
          <strong>
            Confirm routine check done: {outcome.status}
            {outcome.code ? ` (${outcome.code})` : ''}
          </strong>
          <p>{outcome.reason}</p>
        </div>
      )}
    </RoleGuard>
  );
}
